/**
 * Page-lifecycle ping integration · spec 05 §L1509-L1530
 *
 * Single setup at QueryClient init — the SDK adapter (T2204) wires these
 * listeners exactly once. The handlers here are pure helpers; they
 * return the action the transport / state machine should take so they
 * can be unit-tested without a DOM.
 *
 * Page lifecycle table (spec L1509):
 *   visibilitychange:hidden  → pause drop-firehose dispatch; POLL pauses; pins + WS unchanged.
 *   visibilitychange:visible → refetch any query with dataUpdatedAt > staleTime; ping WS — no pong in 5 s → force reconnect.
 *   pageshow + persisted:true → always force reconnect (iOS bfcache restore).
 *
 * Both channels MUST be verified on visible — relying on just one was the
 * v1 regression where iOS bfcache restored a dead WS and POLL kept showing
 * stale data (file 05 §L1517).
 */

import type { ConnectivityState } from '../fsm/state-machine.js';

/** Wait for a pong reply this long before declaring the WS dead. */
export const PING_PONG_TIMEOUT_MS = 5_000;

/**
 * Action returned by a lifecycle handler. The transport / state machine
 * applies the action; the handler itself is pure.
 */
export type LifecycleAction =
  | { kind: 'noop' }
  | { kind: 'pause-dispatch' }
  | { kind: 'resume-and-probe'; pongTimeoutMs: number }
  | { kind: 'force-reconnect'; cause: 'bfcache' | 'pong-timeout' };

export interface VisibilityChangeInput {
  /** 'hidden' | 'visible' — the new document.visibilityState. */
  readonly visibility: 'hidden' | 'visible';
}

export function handleVisibilityChange(input: VisibilityChangeInput): LifecycleAction {
  if (input.visibility === 'hidden') return { kind: 'pause-dispatch' };
  return { kind: 'resume-and-probe', pongTimeoutMs: PING_PONG_TIMEOUT_MS };
}

export interface PageShowInput {
  /** `event.persisted` from the pageshow event. */
  readonly persisted: boolean;
}

export function handlePageShow(input: PageShowInput): LifecycleAction {
  if (input.persisted) return { kind: 'force-reconnect', cause: 'bfcache' };
  return { kind: 'noop' };
}

/**
 * Decide whether the visible-time probe should escalate to a forced
 * reconnect. Called by the transport after waiting `PING_PONG_TIMEOUT_MS`
 * for a pong.
 */
export function pongResultToAction(
  pongReceived: boolean,
  current: ConnectivityState,
): LifecycleAction {
  if (pongReceived) return { kind: 'noop' };
  if (current === 'CONNECTED' || current === 'DEGRADED_NO_REST') {
    return { kind: 'force-reconnect', cause: 'pong-timeout' };
  }
  return { kind: 'noop' };
}

// ─────────────────────────────────────────────────────────────────────────
// T310 · Ping verify after refresh · `nc.flush()` within 2.5 s health probe
// ─────────────────────────────────────────────────────────────────────────
//
// After every JWT refresh AND after every AppState 'active' edge on RN
// (file 05 §2887 deliverable 2), verify the WS is truly alive by sending
// an explicit NATS round-trip and requiring ack within 2.5 s (covers
// cellular round-trip + Synadia ack).
//
// `nats-core@3.x` exposes `nc.flush()` which returns a promise that
// resolves after the server has processed all queued frames — that is
// the round-trip the SDK uses (no public `nc.ping()` API verified
// against `@nats-io/nats-core/lib/core.ts`).

/**
 * 2.5 s timeout — covers cellular round-trip + Synadia ack. Pinned
 * value; RN AppState handler (file 05 §2887) and JWT-refresh
 * post-CONNECT-ack verifier reuse the same threshold.
 */
export const PING_TIMEOUT_MS = 2_500;

/** Minimal connection shape — we only need `flush()`. */
export interface PingableNc {
  flush(): Promise<void>;
}

export interface PingResult {
  readonly alive: boolean;
  /** Round-trip in ms, or -1 on timeout. */
  readonly rttMs: number;
}

/**
 * Send an explicit NATS round-trip and require ack within
 * `PING_TIMEOUT_MS`. Returns `{ alive: true, rttMs }` on ack, otherwise
 * `{ alive: false, rttMs: -1 }`.
 *
 * Pure health probe — does NOT mutate the connection or fire any
 * retry. Force-reconnect on the false return is the caller's
 * responsibility.
 *
 * Injected `now` + `setTimeoutFn` for deterministic tests.
 */
export async function pingAndVerify(
  nc: PingableNc,
  opts: {
    readonly now?: () => number;
    readonly setTimeoutFn?: (h: () => void, ms: number) => unknown;
    readonly clearTimeoutFn?: (h: unknown) => void;
    readonly timeoutMs?: number;
  } = {},
): Promise<PingResult> {
  const now = opts.now ?? ((): number => Date.now());
  const setT =
    opts.setTimeoutFn ??
    ((h: () => void, ms: number): unknown =>
      (globalThis as { setTimeout: typeof setTimeout }).setTimeout(h, ms));
  const clearT =
    opts.clearTimeoutFn ??
    ((h: unknown): void =>
      (globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
        h as ReturnType<typeof setTimeout>,
      ));
  const timeoutMs = opts.timeoutMs ?? PING_TIMEOUT_MS;

  const t0 = now();
  let timer: unknown;
  const timeoutPromise = new Promise<'timeout'>((resolve) => {
    timer = setT(() => resolve('timeout'), timeoutMs);
  });
  // nats-core rejects every pending flush with `RequestError("connection
  // disconnected")` when the socket drops — often after this race has already
  // settled on the timeout, which made the rejection unhandled in every
  // browser tab that lost its connection mid-ping. A rejected flush is simply
  // "not alive".
  const flushPromise = nc.flush().then(
    () => 'ack' as const,
    () => 'nack' as const,
  );

  const result = await Promise.race([flushPromise, timeoutPromise]);
  clearT(timer);
  if (result === 'ack') {
    return { alive: true, rttMs: now() - t0 };
  }
  return { alive: false, rttMs: -1 };
}
